/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Sources/PipelineOutcomeFailingTaskGridSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IReportContext } from "Analytics/Shared/Common/Types";
import { ContributionSource } from "Pipeline/Analytics/Common/Sources/ContributionSource";
import { PipelineReportSource } from "Pipeline/Analytics/Common/Sources/PipelineReportSource";
import { IPipelineOutcomeReportConfiguration } from "Pipeline/Analytics/Common/Types";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Resources";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPipelineRunsData, ITaskData } from "../Types";

export class PipelineOutcomeFailingTaskGridSource extends PipelineReportSource {
    private readonly runsDataProviderName = "ms.vss-build-web.runs-data-provider";

    constructor(pageContext: IVssPageContext) {
        super("PipelineAnalytics_FailingTaskGrid", pageContext);
    }

    public queryPipelineOutcomeFailingTasks(reportContext: IReportContext, confValues: IPipelineOutcomeReportConfiguration): Promise<ITaskData[]> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        if (confValues.stage && confValues.stage.fullName === Resources.AllStagesText) {
            confValues.stage = undefined;
        }
        const filterStr = confValues.stage
            ? super._getTopFailingTaskQueryFilterForStage(reportContext, confValues, confValues.stage.fullName)
            : super._getTopFailingTaskQueryFilter(reportContext, confValues);
        const groupByBuildAndTaskStr = `groupby((${this._getGroupByBuildId()}, ${this._getPipelineJobStageName()}, ${this._getGroupByTask()}), aggregate(${this._getCountForAggregationAsFailedCount()}))`;
        const groupByTaskStr = `groupby((${this._getPipelineJobStageName()}, ${this._getGroupByTask()}), aggregate(${this._getCountForAggregationAsFailedCount()}))`;
        queryOptions.$apply = `${filterStr}/${groupByBuildAndTaskStr}/${groupByTaskStr}`;
        queryOptions.$orderby = ` FailedCount desc `;

        return this._getTopFailingTask(queryOptions);
    }

    public getPipelineRuns(pageContext: IVssPageContext, routeValues?: { [key: string]: string }, ignoreCache?: boolean): Promise<IPipelineRunsData> {
        const source = new ContributionSource({
            pageContext: pageContext
        });

        return source.getDataAsync(this.runsDataProviderName, routeValues, ignoreCache) as Promise<IPipelineRunsData>;
    }

    public async queryFailingPipelineRunIdsForTask(
        reportContext: IReportContext,
        confValues: IPipelineOutcomeReportConfiguration,
        stageName:string,
        taskName: string
    ): Promise<number[]> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        if (confValues.stage && confValues.stage.fullName === Resources.AllStagesText) {
            confValues.stage = undefined;
        }
        const filterStr = super._getFailedPipelineRunsForTaskQueryFilter(reportContext, confValues, taskName, stageName)
        const groupByBuildAndTaskStr = `groupby((${this._getGroupByBuildId()}), aggregate(${this._getCountForAggregationAsFailedCount()}))`;
        queryOptions.$apply = `${filterStr}/${groupByBuildAndTaskStr}`;
        queryOptions.$orderby = ` FailedCount desc `;
        return this._getPipelineRunsIds(queryOptions);
    }

    private async _getTopFailingTask(queryOptions: ODataQueryOptions): Promise<ITaskData[]> {
        const data = await this.queryOData(queryOptions);
        const failingTasksData: ITaskData[] = [];

        if (!data || !data.value) {
            throw new Error("data is empty.");
        }

        let dataValues = data.value as any[];
        if (dataValues && dataValues.length > 0) {
            dataValues = dataValues.slice(0, 10);
            dataValues.forEach(d => {
                var taskAndStage = {
                    stageName: d.PipelineJob.StageName,
                    stageDisplayName: this._parseStageName(d.PipelineJob.StageName),
                    taskName: this._parseTaskDisplayName(d)
                };
                failingTasksData.push(taskAndStage);
            });
        }
        return failingTasksData;
    }

    private async _getPipelineRunsIds(queryOptions: ODataQueryOptions): Promise<number[]> {
        const data = await this.queryOData(queryOptions);
        let buildIds: number[] = [];

        if (!data || !data.value) {
            throw new Error("data is empty.");
        }
        const dataValues = data.value as any[];
        const buildId: number[] = [];

        if (dataValues && dataValues.length > 0) {
            dataValues.forEach(d => {
                buildId.push(parseInt(this._parseBuildId(d)));
            });
        }

        buildIds = buildId;
        return buildIds;
    }
}
